import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cn } from '@/lib/cn';

export interface LabelProps extends ComponentPropsWithoutRef<typeof LabelPrimitive.Root> {
  /** Añade el asterisco de campo obligatorio con su texto para lectores. */
  required?: boolean;
}

export const Label = forwardRef<ElementRef<typeof LabelPrimitive.Root>, LabelProps>(function Label(
  { className, required, children, ...props },
  ref,
) {
  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn(
        'text-sm font-medium leading-none text-fg',
        'peer-disabled:cursor-not-allowed peer-disabled:text-fg-disabled',
        className,
      )}
      {...props}
    >
      {children}
      {required ? (
        <span className="ml-0.5 text-danger" aria-hidden="true">
          *
        </span>
      ) : null}
      {required ? <span className="sr-only"> (obligatorio)</span> : null}
    </LabelPrimitive.Root>
  );
});

/** Texto de ayuda o de error bajo un campo. */
export function FieldHint({
  children,
  error,
  id,
}: {
  children?: React.ReactNode;
  error?: string | null;
  id?: string;
}) {
  if (error) {
    return (
      <p id={id} role="alert" className="text-xs text-danger">
        {error}
      </p>
    );
  }
  if (!children) return null;
  return (
    <p id={id} className="text-xs text-fg-subtle">
      {children}
    </p>
  );
}

/** Envoltorio de campo: etiqueta, control y ayuda con la separación estándar. */
export function Field({
  label,
  htmlFor,
  required,
  hint,
  error,
  className,
  children,
}: {
  label?: string;
  htmlFor?: string;
  required?: boolean;
  hint?: React.ReactNode;
  error?: string | null;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label ? (
        <Label htmlFor={htmlFor} required={required}>
          {label}
        </Label>
      ) : null}
      {children}
      <FieldHint id={htmlFor ? `${htmlFor}-hint` : undefined} error={error}>
        {hint}
      </FieldHint>
    </div>
  );
}
